import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Checkbox,
    FormControlLabel,
    FormControl,
    RadioGroup,
    Radio,
    FormHelperText,
    Typography,
    Tooltip,
    Autocomplete,
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';

interface Props {
    open: boolean;
    brokerList: { value: number; label: string }[];
    templateList: { value: number; label: string }[];
    lobList: { label: string, value: string }[];
    onClose: () => void;
    handleSubmit: (form: any) => void;
}

const radioLabelStyle = {
    "& .MuiFormControlLabel-label": {
        fontSize: "0.875rem",
    },
};

const AddStpMaster: React.FC<Props> = ({ open, onClose, handleSubmit, brokerList, templateList, lobList }) => {
    const [formData, setFormData] = useState({
        brokerId: 0,
        templateName: "",
        lobCode: "",
        jobType: "",
        arEnabled: false,
        ssdEnabled: false,
        msdEnabled: false,
        includeEndorsement: false,
        metaData: "",
        carrierName: "",
        ignoreStp: false
    });

    const [errors, setErrors] = useState<any>({});

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validate = () => {
        let tempErrors: any = {};

        if (!formData.brokerId) tempErrors.brokerId = "Broker is required";
        if (!formData.templateName) tempErrors.templateName = "Template is required";
        if (!formData.lobCode) tempErrors.lobCode = "LOB Code is required";
        if (!formData.jobType) tempErrors.jobType = "Select one Job Type";

        if ((formData.msdEnabled || formData.ssdEnabled) && !formData.metaData) tempErrors.metaData = "Meta data is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const onSubmit = () => {
        if (!validate()) return;
        handleSubmit(formData);
        console.log("Submitted Data:", formData);

    };

    const isMetaEnabled = formData.ssdEnabled || formData.msdEnabled;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} mt={1}>

                    {/* Broker Dropdown */}
                    <Grid item xs={12} md={4}>
                        <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.brokerId}
                        >
                            <Typography
                                variant="body2"
                                sx={{ mb: 0.5, fontWeight: 500 }}
                            >
                                Select Broker
                            </Typography>
                            <Autocomplete
                                options={brokerList}
                                getOptionLabel={(option) => option?.label || ''}
                                value={brokerList.find((e: any) => e.value === formData.brokerId) || null}
                                onChange={(event, newValue) => {
                                    handleChange("brokerId", newValue?.value)
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Select Broker..."
                                        size="medium"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { height: 25, fontSize: "11px" }
                                        }}
                                    />
                                )}
                                fullWidth
                                slotProps={{ listbox: { sx: { fontSize: '11px' } } }}
                            />

                            {errors.brokerId && (
                                <FormHelperText>{errors.brokerId}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>


                    {/* Template */}
                    <Grid item xs={12} md={4}>
                        <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.templateName}
                        >
                            <Typography
                                variant="body2"
                                sx={{ mb: 0.5, fontWeight: 500 }}
                            >
                                Select Template
                            </Typography>
                            <Autocomplete
                                options={templateList}
                                getOptionLabel={(option) => option?.label || ''}
                                value={templateList.find((e: any) => e.label === formData.templateName) || null}
                                onChange={(event, newValue) => {
                                    handleChange("templateName", newValue?.label);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Select Template..."
                                        size="medium"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { height: 25, fontSize: "11px" }
                                        }}
                                    />
                                )}
                                fullWidth
                                slotProps={{ listbox: { sx: { fontSize: '11px' } } }}
                            />

                            {errors.templateName && (
                                <FormHelperText>{errors.templateName}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>


                    {/* LOB */}
                    <Grid item xs={12} md={4}>
                        <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.lobCode}
                        >
                            <Typography
                                variant="body2"
                                sx={{ mb: 0.5, fontWeight: 500 }}
                            >
                                Select Lob
                            </Typography>
                            <Autocomplete
                                options={lobList}
                                getOptionLabel={(option) => option?.label || ''}
                                value={lobList.find((e: any) => e.label === formData.lobCode) || null}
                                onChange={(event, newValue) => {
                                    handleChange("lobCode", newValue?.label)
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Select LOB..."
                                        size="medium"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { height: 25, fontSize: "11px" }
                                        }}
                                    />
                                )}
                                fullWidth
                                slotProps={{ listbox: { sx: { fontSize: '11px' } } }}
                            />

                            {errors.lobCode && (
                                <FormHelperText>{errors.lobCode}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>


                    {/* Job Type (Exactly One) */}
                    <Grid item xs={12}>
                        <Typography
                            variant="body2"
                            sx={{ mb: 0.5, fontWeight: 500 }}
                        >Select Job Type</Typography>
                        <FormControl error={!!errors.jobType}>
                            <RadioGroup
                                row
                                value={formData.jobType}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    setFormData((prev) => ({
                                        ...prev,
                                        jobType: value,
                                        arEnabled: value === "AR",
                                        ssdEnabled: value === "SSD",
                                        msdEnabled: value === "MSD",
                                    }));
                                }}
                            >
                                <FormControlLabel sx={radioLabelStyle} value="AR" control={<Radio size="small" />} label="Enable AR" />
                                <FormControlLabel sx={radioLabelStyle} value="SSD" control={<Radio size="small" />} label="Enable SSD" />
                                <FormControlLabel sx={radioLabelStyle} value="MSD" control={<Radio size="small" />} label="Enable MSD" />
                            </RadioGroup>
                            {errors.jobType && (
                                <FormHelperText>{errors.jobType}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Include Endorsement */}
                    <Grid item xs={12}>
                        <div>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.includeEndorsement}
                                        onChange={(e) =>
                                            handleChange("includeEndorsement", e.target.checked)
                                        }
                                    />
                                }
                                label="Include Endorsement"
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                    },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.ignoreStp}
                                        onChange={(e) =>
                                            handleChange("ignoreStp", e.target.checked)
                                        }
                                    />
                                }
                                label="Ignore STP"
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                    },
                                }}
                            />
                        </div>
                    </Grid>

                    {/* MetaData */}
                    <Grid item xs={12} md={6}>
                        <FormControl error={!!errors.metaData}>
                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                >MetaData</Typography>
                                <Tooltip title="Enabled only when SSD or MSD is selected" placement="right-start">
                                    <InfoIcon />
                                </Tooltip>
                            </div>
                            <TextField
                                fullWidth
                                placeholder="MetaData"
                                size="small"
                                value={formData.metaData}
                                onChange={(e) =>
                                    handleChange("metaData", e.target.value)
                                }
                                disabled={!isMetaEnabled}
                            />
                            {errors.metaData && (
                                <FormHelperText>{errors.metaData}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Text Area */}
                    <Grid item xs={12}>
                        <FormControl error={!!errors.carrierName} fullWidth>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                            >Carrier Name</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.carrierName}
                                onChange={(e) =>
                                    handleChange("carrierName", e.target.value)
                                }
                                slotProps={{
                                    input: {
                                        sx: {
                                            fontSize: 12,
                                        },
                                    },
                                }}
                                sx={{
                                    "& textarea": {
                                        fontSize: 12,
                                        overflowY: "auto",
                                    },
                                }}
                            />
                            {errors.metaData && (
                                <FormHelperText>{errors.carrierName}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onSubmit}>
                    submit
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default AddStpMaster;
